import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { api, clearSession, onSessionEnded, setSession } from '../api/client'
import type { MeResponse, UserRole } from '../api/types'

/**
 * Session state for the panel.
 *
 * The panel is staff-only, so the gate is a role check rather than a separate
 * login system: the same account signs into the app and the panel, and anything
 * below MODERATOR is turned away after authenticating.
 */

interface AuthState {
  user: MeResponse | null
  /** True until the stored session has been checked, so routes do not flash. */
  loading: boolean
  signIn: (email: string, code: string) => Promise<void>
  signOut: () => Promise<void>
  can: (minimum: UserRole) => boolean
}

const ROLE_ORDER: UserRole[] = [
  'USER',
  'MODERATOR',
  'SENIOR_MODERATOR',
  'ADMIN',
  'SUPER_ADMIN',
]

/** Mirrors the cumulative permission levels documented on screen 7d. */
export function roleAtLeast(role: UserRole, minimum: UserRole): boolean {
  return ROLE_ORDER.indexOf(role) >= ROLE_ORDER.indexOf(minimum)
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<MeResponse | null>(null)
  const [loading, setLoading] = useState(true)

  // Restore on reload, and react to the client giving up on a refresh.
  useEffect(() => {
    let cancelled = false

    void api.auth.restore().then((tokens) => {
      if (cancelled) return
      setUser(tokens?.user ?? null)
      setLoading(false)
    })

    const unsubscribe = onSessionEnded(() => setUser(null))
    return () => {
      cancelled = true
      unsubscribe()
    }
  }, [])

  const signIn = useCallback(async (email: string, code: string) => {
    const tokens = await api.auth.verifyCode(email, code)

    // Authenticating succeeded; being allowed in is a separate question.
    if (!roleAtLeast(tokens.user.role, 'MODERATOR')) {
      clearSession()
      throw new Error('Bu hesabın moderasiya panelinə girişi yoxdur.')
    }

    setSession(tokens)
    setUser(tokens.user)
  }, [])

  const signOut = useCallback(async () => {
    // Best effort: a failed revoke must not trap the user in the panel.
    await api.auth.logout().catch(() => undefined)
    clearSession()
    setUser(null)
  }, [])

  const can = useCallback(
    (minimum: UserRole) => (user ? roleAtLeast(user.role, minimum) : false),
    [user],
  )

  const value = useMemo<AuthState>(
    () => ({ user, loading, signIn, signOut, can }),
    [user, loading, signIn, signOut, can],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>')
  return context
}
